import React from 'react';
import { T, FONT_STACK } from '../customer/customers.theme';
import { Spinner } from '../customer/CustomersUI';

/**
 * The few controls the menu and administration screens need on top of the kit
 * the customers book already carries.
 *
 * The dialog, the pill field and the buttons live in `containers/customer/
 * CustomersUI.js` — that is where they were first needed, not a statement that
 * they belong to customers. They are imported from there rather than copied,
 * so one change to a button changes every screen.
 */

const base = (size) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
});

export const ImageIcon = ({ size = 20, width = 1.8 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <rect x="3.5" y="5" width="17" height="14" rx="2.5" />
    <circle cx="9" cy="10" r="1.6" />
    <path d="M4 17l4.5-4.5 3.5 3.5 3-3 5 5" />
  </svg>
);

export const PrinterIcon = ({ size = 14, width = 1.8 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <path d="M7 9V3.5h10V9M7 18.5H5.5a2 2 0 0 1-2-2V11a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2v5.5a2 2 0 0 1-2 2H17" />
    <rect x="7" y="14" width="10" height="6.5" rx="1" />
  </svg>
);

export const TagIcon = ({ size = 24, width = 1.7 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <path d="M4 11.2V4.5h6.7l8.8 8.8-6.7 6.7z" />
    <circle cx="8" cy="8" r="1.4" />
  </svg>
);

export const UsersIcon = ({ size = 24, width = 1.7 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <circle cx="9.5" cy="8.5" r="3.6" />
    <path d="M3 19.5c0-3.2 2.9-5.3 6.5-5.3s6.5 2.1 6.5 5.3" />
    <path d="M16.5 5.4a3.6 3.6 0 0 1 0 6.9M18 14.6c2.2.5 3.7 2 3.7 4.2" />
  </svg>
);

/* ── controls ───────────────────────────────────────────────────────────── */

/**
 * A native select in the same pill the text fields use.
 *
 * Native because a category list is short, the keyboard already works, and a
 * hand-built listbox is a week of work to make as good as the one the operating
 * system ships.
 */
export function SelectField({ label, name, value, onChange, disabled, invalid, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label ? (
        <label htmlFor={name} style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>
          {label}
        </label>
      ) : null}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          height: 46,
          padding: '0 8px 0 16px',
          background: disabled ? T.sunk : T.surface,
          border: `${invalid ? 1.5 : 1}px solid ${invalid ? T.brand : T.borderStrong}`,
          borderRadius: T.radiusPill,
          boxSizing: 'border-box',
        }}
      >
        <select
          id={name}
          name={name}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          style={{
            flex: 1,
            minWidth: 0,
            height: '100%',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            font: 'inherit',
            fontFamily: FONT_STACK,
            fontSize: 14,
            color: value ? T.ink : T.inkFaint,
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          {children}
        </select>
      </div>
    </div>
  );
}

/**
 * The picture tile.
 *
 * Square, clickable, and it shows what it holds: the chosen file, the picture
 * already on the item, or an invitation. The phone puts a 60px tile beside the
 * category dropdown; here it is bigger because there is room and because the
 * whole point of the field is seeing the photo.
 */
export function ImageField({ src, busy, progress, disabled, onPick, onClear }) {
  const inputRef = React.useRef(null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>Image</span>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <button
          type="button"
          onClick={() => !disabled && !busy && inputRef.current && inputRef.current.click()}
          disabled={disabled || busy}
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 92,
            height: 92,
            padding: 0,
            background: src ? T.shade : T.sunk,
            border: `1px ${src ? 'solid' : 'dashed'} ${T.borderStrong}`,
            borderRadius: T.radiusPanel,
            color: T.inkFaint,
            cursor: disabled || busy ? 'not-allowed' : 'pointer',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {src ? (
            <img
              src={src}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <ImageIcon size={26} />
          )}

          {busy ? (
            <span
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                background: 'rgba(255,255,255,0.82)',
                color: T.inkSoft,
                fontSize: 11.5,
                fontWeight: 700,
              }}
            >
              <Spinner size={13} />
              {progress ? <span>{progress}%</span> : null}
            </span>
          ) : null}
        </button>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <span style={{ fontSize: 12, color: T.inkFaint, lineHeight: 1.5 }}>
            {busy
              ? 'Uploading…'
              : src
              ? 'Click the tile to replace it.'
              : 'JPG, PNG or WebP. Optional.'}
          </span>

          {src && !busy ? (
            <button
              type="button"
              onClick={onClear}
              disabled={disabled}
              style={{
                alignSelf: 'flex-start',
                padding: 0,
                background: 'transparent',
                border: 'none',
                font: 'inherit',
                fontFamily: FONT_STACK,
                fontSize: 12.5,
                fontWeight: 600,
                color: T.danger,
                cursor: 'pointer',
              }}
            >
              Remove image
            </button>
          ) : null}
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={(event) => {
          const file = event.target.files && event.target.files[0];
          // Reset first: picking the same file twice must still fire a change.
          event.target.value = '';
          if (file) onPick(file);
        }}
      />
    </div>
  );
}

/**
 * The picture, or two big letters when there is none.
 *
 * The phone draws a coloured circle with one letter. A till shows the menu as a
 * grid of cards all day, and a card is easier to hit when the whole tile is the
 * picture — so the letters fill the tile instead.
 */
export function Thumbnail({ src, initials, height = 118, radius = 0 }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height,
        background: T.shade,
        borderRadius: radius,
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {src ? (
        <img
          src={src}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <span
          style={{
            fontSize: Math.round(height * 0.34),
            fontWeight: 700,
            letterSpacing: 0.5,
            color: T.inkGhost,
          }}
        >
          {initials}
        </span>
      )}
    </div>
  );
}

/** The search box the menu and the user list share. */
export function SearchField({ value, onChange, placeholder, icon }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        height: 42,
        padding: '0 14px',
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: T.radiusPill,
        boxSizing: 'border-box',
      }}
    >
      <span style={{ display: 'flex', color: T.inkFaint }}>{icon}</span>
      <input
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        style={{
          flex: 1,
          minWidth: 0,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          font: 'inherit',
          fontFamily: FONT_STACK,
          fontSize: 13.5,
          color: T.ink,
        }}
      />
    </div>
  );
}

/** Nothing to show, said once and the same way everywhere. */
export function EmptyState({ icon, title, action }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        padding: 60,
        textAlign: 'center',
      }}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 56,
          height: 56,
          border: `1.5px dashed ${T.borderStrong}`,
          borderRadius: T.radiusPill,
          color: T.inkGhost,
        }}
      >
        {icon}
      </span>
      <span style={{ fontSize: 15, color: T.inkSoft }}>{title}</span>
      {action || null}
    </div>
  );
}

/** A centred spinner with a word beside it. */
export function LoadingLine({ label = 'Loading…' }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 9,
        padding: 50,
        color: T.inkFaint,
      }}
    >
      <Spinner size={15} />
      <span style={{ fontSize: 13 }}>{label}</span>
    </div>
  );
}
